import React, { useEffect, useState } from "react";
import { Loader2, ExternalLink, Flame, ShieldAlert, Award, Globe, DollarSign, Calendar } from "lucide-react";
import api from "../../lib/api";
import DiscoverLayout from "../../components/discover/DiscoverLayout";
import SearchBar from "../../components/discover/SearchBar";
import FilterBar from "../../components/discover/FilterBar";
import Pagination from "../../components/discover/Pagination";
import { motion } from "framer-motion";

export default function StartupNewsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [categoryFilter, setCategoryFilter] = useState("All");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/discover/stories?limit=20")
      .then((r) => {
        if (alive) {
          // Enrich with categorizations: Funding, Acquisition, IPO, Market Update
          const fetchedNews = r.data.items || [];
          const categoriesList = ["Funding", "Acquisition", "IPO", "Market Update"];
          
          const enriched = fetchedNews.map((news, idx) => {
            let category = "Market Update";
            if (news.funding) {
              category = "Funding";
            } else if (news.title.toLowerCase().includes("acquire") || news.title.toLowerCase().includes("buy") || news.title.toLowerCase().includes("purchase")) {
              category = "Acquisition";
            } else if (news.title.toLowerCase().includes("ipo") || news.title.toLowerCase().includes("listing") || news.title.toLowerCase().includes("shares")) {
              category = "IPO";
            } else {
              category = categoriesList[idx % categoriesList.length];
            }
            return {
              ...news,
              category,
            };
          });
          setItems(enriched);
        }
      })
      .catch((e) => console.error("Error loading news stories:", e))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  const filtered = items.filter((item) => {
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      item.title.toLowerCase().includes(q) ||
      (item.startup && item.startup.toLowerCase().includes(q)) ||
      item.source.toLowerCase().includes(q);

    const matchesCategory =
      categoryFilter === "All" || item.category === categoryFilter;

    return matchesSearch && matchesCategory;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const featuredStory = filtered[0];
  const listStories = filtered.slice(1);

  return (
    <DiscoverLayout
      title="Startup News & Funding"
      subtitle="Track live funding rounds, major corporate acquisitions, tech IPO roadmaps, and macro market updates in the Indian startup ecosystem."
    >
      <div className="space-y-8">
        {/* Search and Categories bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-gray-150 shadow-sm">
          <div className="w-full md:w-80">
            <SearchBar
              value={searchQuery}
              onChange={(q) => {
                setSearchQuery(q);
                setCurrentPage(1);
              }}
              placeholder="Search news by title, source, startup..."
            />
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto shrink-0 scrollbar-none">
            {["All", "Funding", "Acquisition", "IPO", "Market Update"].map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setCategoryFilter(cat);
                  setCurrentPage(1);
                }}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
                  categoryFilter === cat
                    ? "bg-[#7C3AED] text-white shadow-sm"
                    : "bg-gray-50 border border-gray-255 hover:bg-gray-100 text-gray-700"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
            <p className="text-sm text-gray-500 font-medium">No stories match your search filters.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Featured Post Card */}
            {currentPage === 1 && featuredStory && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-[24px] border border-gray-150 p-6 md:p-8 shadow-sm flex flex-col md:flex-row justify-between gap-6 hover:shadow-md transition-all duration-300 relative group overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-2.5 h-full bg-gradient-to-b from-[#7C3AED] to-[#EC4899]" />
                <div className="space-y-4 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] font-bold text-white bg-gradient-to-r from-[#7C3AED] to-[#EC4899] px-3 py-1 rounded-full uppercase tracking-wider">
                      Featured
                    </span>
                    <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-100/50 px-3 py-1 rounded-full">
                      {featuredStory.category}
                    </span>
                    {featuredStory.funding && (
                      <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-100/50 px-3 py-1 rounded-full">
                        💰 {featuredStory.funding}
                      </span>
                    )}
                  </div>

                  <h2 className="font-display font-bold text-gray-950 text-xl md:text-2xl leading-tight group-hover:text-[#7C3AED] transition">
                    {featuredStory.title}
                  </h2>

                  <div className="flex items-center gap-2.5 text-xs text-gray-400">
                    <span className="font-semibold text-gray-600 bg-gray-50 px-2 py-0.5 rounded border border-gray-100">{featuredStory.source}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-gray-300" /> Live Feed</span>
                  </div>
                </div>

                <div className="flex items-center shrink-0 self-start md:self-center">
                  <a
                    href={featuredStory.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3.5 rounded-full bg-indigo-50 hover:bg-[#7C3AED] hover:text-white text-[#7C3AED] shadow-sm hover:shadow transition duration-200"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                </div>
              </motion.div>
            )}

            {/* Stories Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(currentPage === 1 ? listStories : paginatedItems).map((story, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -4 }}
                  className="bg-white rounded-[20px] border border-gray-150 p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition duration-300 group"
                >
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[9px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-100/50 px-2 py-0.5 rounded">
                        {story.category}
                      </span>
                      {story.funding && (
                        <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-100/50 px-2 py-0.5 rounded">
                          💰 {story.funding}
                        </span>
                      )}
                    </div>

                    <h3 className="font-display font-semibold text-gray-900 text-sm leading-snug group-hover:text-[#7C3AED] transition line-clamp-3">
                      {story.title}
                    </h3>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[10px] text-gray-400">
                      <span className="font-bold text-gray-600">{story.source}</span>
                    </div>

                    <a
                      href={story.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-xl bg-gray-50 text-gray-400 hover:text-[#7C3AED] hover:bg-indigo-50 transition"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </DiscoverLayout>
  );
}
